import React from 'react';
import { SearchInput, Spinner } from '@parsina/framework';
import { C, CT, SHADOW } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { bffQuery } from '../../lib/bffClient.js';
import { LIST_DRUGS, SEARCH_DEBOUNCE_MS, SEARCH_LIMIT } from './Prescription.shared.js';
import { MIN_SEARCH_LENGTH } from '../../lib/patients.js';

// The formulary picker.
//
// It searches the REAL formulary through `listDrugs`. The doctor app's
// standalone نسخه screen ships a hardcoded twenty-drug array instead; that is
// the thing this must not copy.
//
// What comes back carries `id` — a stable slug like 'acetaminophen' — and that
// slug is the interaction key. The display name never leaves this component.

const DrugCombobox = ({ onAdd }) => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const boxRef = React.useRef(null);
  // The popup has to be REFERABLE for the input to declare what it controls.
  const listId = React.useId();

  React.useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setSearching(false);
      setFailed(false);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(() => {
      bffQuery(LIST_DRUGS, { query: term, limit: SEARCH_LIMIT })
        .then((data) => {
          if (!alive) return;
          setResults(data?.listDrugs || []);
          setFailed(false);
        })
        .catch((err) => {
          if (!alive) return;
          console.error('[drugs] search failed:', err?.message);
          setResults([]);
          setFailed(true);
        })
        .finally(() => {
          if (alive) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query]);

  React.useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const select = (drug) => {
    onAdd(drug);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const term = query.trim();
  const showPanel = open && term.length > 0;

  // What the live region below says. The popup itself is conditionally mounted,
  // so its own text can never be announced reliably — a live region has to be in
  // the DOM before its content changes. This one always is.
  const searchable = term.length >= MIN_SEARCH_LENGTH;
  let announcement = '';
  if (showPanel && searchable) {
    if (searching) announcement = t('prescription.search.searching');
    else if (failed) announcement = t('errors.load.drugs');
    else if (results.length === 0) announcement = t('prescription.search.empty');
    else announcement = t('prescription.search.results', { count: results.length });
  }

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <span
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {announcement}
      </span>
      <SearchInput
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Escape closes the popup and keeps focus in the field. Without it the
        // only ways out were a mouse click elsewhere or picking a drug — a panel
        // that covers the prescription below it and cannot be dismissed from the
        // keyboard.
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }
        }}
        placeholder={t('prescription.search.placeholder')}
        aria-label={t('prescription.search.label')}
        clearLabel={t('common.actions.clearFilters')}
        // The popup already declared role="listbox"/role="option", but nothing
        // tied it to the field — so its appearance, its result count and its
        // "no medicine matches" were all invisible to a screen reader. These go
        // through `inputProps` because that is the prop the kit forwards to the
        // real <input> (its own `...rest` lands on the wrapper).
        inputProps={{
          // The kit hardcodes dir="rtl" on its <input>; a formulary is searched
          // by Persian brand name AND by Latin generic name. `dir="auto"`
          // follows what was typed.
          dir: 'auto',
          role: 'combobox',
          'aria-expanded': showPanel,
          // Only while it exists — a dangling reference is worse than none.
          'aria-controls': showPanel ? listId : undefined,
          'aria-autocomplete': 'list',
        }}
      />

      {showPanel && (
        <div
          id={listId}
          role="listbox"
          aria-label={t('prescription.search.label')}
          style={{
            position: 'absolute',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            top: 'calc(100% + 6px)',
            zIndex: 30,
            background: CT.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            boxShadow: SHADOW.floating,
            maxHeight: 300,
            overflowY: 'auto',
            padding: 6,
          }}
        >
          {term.length < MIN_SEARCH_LENGTH && (
            <div style={{ padding: 12, fontSize: 12.5, color: C.textMuted }}>
              {t('prescription.search.hint')}
            </div>
          )}

          {term.length >= MIN_SEARCH_LENGTH && searching && (
            <div style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner size={16} />
              <span style={{ fontSize: 12.5, color: C.textMuted }}>
                {t('prescription.search.searching')}
              </span>
            </div>
          )}

          {term.length >= MIN_SEARCH_LENGTH && !searching && failed && (
            <div style={{ padding: 12, fontSize: 12.5, color: CT.dangerInk }}>
              {t('errors.load.drugs')}
            </div>
          )}

          {term.length >= MIN_SEARCH_LENGTH && !searching && !failed && results.length === 0 && (
            <div style={{ padding: 12, fontSize: 12.5, color: C.textMuted }}>
              {t('prescription.search.empty')}
            </div>
          )}

          {results.map((drug) => (
            <button
              key={drug.id}
              type="button"
              role="option"
              aria-selected="false"
              onClick={() => select(drug)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'start',
                background: 'none',
                border: 'none',
                borderRadius: 9,
                padding: '9px 12px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = CT.surface2;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>{drug.name}</div>
              <div style={{ fontSize: 11.5, color: C.textMuted }}>
                {[drug.genericName, drug.category].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DrugCombobox;
